import { useState, useEffect, useCallback } from 'react';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import {
  GetHelpArticleAction,
  GetHelpLayoutAction,
  ListHelpArticlesAction,
  ListHelpCategoriesAction,
  SaveHelpArticleAction,
  SaveHelpCategoryAction,
  SaveHelpLayoutAction,
} from '@/application/actions/help/HelpDocumentationActions';
import type {
  CreateArticlePayload,
  CreateCategoryPayload,
  HelpArticleDTO,
  HelpCategoryDTO,
  HelpLayoutDTO,
  ListArticlesParams,
  SaveHelpLayoutPayload,
  UpdateArticlePayload,
  UpdateCategoryPayload,
} from '@/domain/repositories/IHelpDocumentationRepository';

export function useHelpCategories() {
  const [categories, setCategories] = useState<HelpCategoryDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const action = container.get<ListHelpCategoriesAction>(TYPES.ListHelpCategoriesAction);
      setCategories(await action.execute());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Error al cargar categorías'));
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveAction = () => container.get<SaveHelpCategoryAction>(TYPES.SaveHelpCategoryAction);

  return {
    categories,
    isLoading,
    error,
    refetch: load,
    createCategory: (data: CreateCategoryPayload) => saveAction().create(data),
    updateCategory: (id: string, data: UpdateCategoryPayload) => saveAction().update(id, data),
    deleteCategory: (id: string) => saveAction().delete(id),
  };
}

export function useHelpArticles(params: ListArticlesParams) {
  const [articles, setArticles] = useState<HelpArticleDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const action = container.get<ListHelpArticlesAction>(TYPES.ListHelpArticlesAction);
      setArticles(await action.execute(params));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Error al cargar artículos'));
      setArticles([]);
    } finally {
      setIsLoading(false);
    }
  }, [params.locale, params.contentType, params.categoryId, params.published]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveAction = () => container.get<SaveHelpArticleAction>(TYPES.SaveHelpArticleAction);

  return {
    articles,
    isLoading,
    error,
    refetch: load,
    createArticle: (data: CreateArticlePayload) => saveAction().create(data),
    updateArticle: (id: string, data: UpdateArticlePayload) => saveAction().update(id, data),
    deleteArticle: (id: string) => saveAction().delete(id),
    updateSortBySlug: (slug: string, sortOrder: number) =>
      saveAction().updateSortBySlug(slug, sortOrder),
  };
}

export function useHelpArticle(id: string | undefined) {
  const [article, setArticle] = useState<HelpArticleDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!id || id === 'new') {
      setArticle(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const action = container.get<GetHelpArticleAction>(TYPES.GetHelpArticleAction);
      setArticle(await action.execute(id));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Error al cargar el artículo'));
      setArticle(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  return { article, isLoading, error, refetch: load };
}

export function useHelpLayout() {
  const [layout, setLayout] = useState<HelpLayoutDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const action = container.get<GetHelpLayoutAction>(TYPES.GetHelpLayoutAction);
      setLayout(await action.execute());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Error al cargar el layout'));
      setLayout(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (data: SaveHelpLayoutPayload) => {
    const action = container.get<SaveHelpLayoutAction>(TYPES.SaveHelpLayoutAction);
    const saved = await action.execute(data);
    setLayout(saved);
    return saved;
  };

  return { layout, isLoading, error, refetch: load, saveLayout: save };
}
